import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

import CampaignChatModal from "./CampaignChatModal";
import { notifyUser } from "../../utils/notifyUser";

// Add CSS for custom scrollbar
const scrollbarStyles = `
  .mobile-scroll-container::-webkit-scrollbar {
    height: 16px;
        <button
          onClick={() => {
            if (!campaigns || campaigns.length === 0 || campaigns.every(c => c.submitted !== 1)) {
              navigate('/campaign-page');
              return;
            }
            // If any campaign has submitted === 1, go to campaign-summary
            navigate('/campaign-summary');
          }}
          style={{
            backgroundColor: '#D4AF37',
            color: 'var(--navy)',
            border: 'none',
            borderRadius: '6px',
            padding: '12px 32px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'background 0.2s',
          }}
        >
          Campaigns
        </button>
  }
  
  .mobile-scroll-container::-webkit-scrollbar {
    -webkit-appearance: none;
  }
`;

// Inject styles into document head
if(typeof document !== "undefined") {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = scrollbarStyles;
    document.head.appendChild(styleSheet);
}

const CampaignList = ({ user }) => {
    CampaignList.propTypes = {
        user: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }).isRequired,
    };
    // State to control table visibility
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState([]);
    const [, setNoEntrepreneurRecord] = useState(false);
    const [applicationStatus, setApplicationStatus] = useState(null);
    const [checkingIssuerAgreement, setCheckingIssuerAgreement] =
        useState(false);

    // Responsive state management
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const fetchCampaigns = async () => {
            try {
                // const userData = JSON.parse(localStorage.getItem("user"));
                const userId = user?.id;

                if(!userId) {
                    console.error("User ID not found in localStorage.");
                    notifyUser(null, "error", "Unable to load campaigns. Please refresh the page.", "toast", null, null, null, null, null, false, false, "top-center");
                    return;
                }

                console.log("Fetching campaigns for userId:", userId);
                const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/accounts/entrepreneur/${userId}/campaigns`,
                    {
                        credentials: "include",
                    },
                );
                const data = await res.json();

                console.log("Campaign fetch response:", res.status, data);

                if(!res.ok) {
                    if(
                        res.status === 404 &&
                        data.message === "Entrepreneur not found"
                    ) {
                        console.log(
                            "No entrepreneur record found - user needs to complete account setup",
                        );
                        notifyUser(null, "info", "Please complete your entrepreneur account setup first.", "toast", null, null, null, null, null, false, false, "top-center");
                        setNoEntrepreneurRecord(true);
                        setCampaigns([]);
                        return;
                    }
                    throw new Error(
                        data.message || "Failed to fetch campaigns",
                    );
                }

                // Reset the flag if we successfully fetch data
                setNoEntrepreneurRecord(false);

                if(!Array.isArray(data)) {
                    console.error("API did not return an array:", data);
                    notifyUser(null, "error", "Unexpected data format received.", "toast", null, null, null, null, null, false, false, "top-center");
                    return;
                }
                const formatted = data.map((item) => ({
                    id: item.id,
                    name: item.name,
                    dateSubmitted: item.dateSubmitted || null,
                    status: item.status,
                    raised: item.raised || 0,
                    percentageRaised: item.percentageRaised || 0,
                    daysRemaining: item.daysRemaining || 0,
                    goal: item.goal || 1,
                    fundingBegins: item.fundingBegins || null,
                    submitted:
                        item.submitted !== undefined ? item.submitted : 1,
                }));

                console.log("Formatted campaigns:", formatted);
                setCampaigns(formatted);

                // Additional debugging for empty campaigns
                if(formatted.length === 0) {
                    console.log(
                        "No campaigns found for user. User may need to complete application submission.",
                    );
                }
            } catch(err) {
                console.error("Error fetching campaigns", err);
                console.error(
                    "Full error details:",
                    err.message,
                    err.response?.data,
                );
                notifyUser(null, "error", "Failed to load campaigns. Please try again later.", "toast", null, null, null, null, null, false, false, "top-center");
                setCampaigns([]);
            }
        };

        fetchCampaigns();
    }, [user]);

    useEffect(() => {
        const fetchApplicationStatus = async () => {
            try {
                const userId = user?.id;

                if(!userId) return;

                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/entrepreneur/${userId}/has-campaigns`,
                    {
                        credentials: "include",
                    },
                );
                const data = await response.json();

                if(response.ok) {
                    setApplicationStatus(data.applicationStatus);
                }
            } catch(err) {
                console.error("Error fetching application status", err);
            }
        };

        fetchApplicationStatus();
    }, [user]);

    // Modal state and handlers
    const [chatModalOpen, setChatModalOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);

    const handleCloseChatModal = () => {
        setChatModalOpen(false);
        setSelectedCampaign(null);
    };

    const handleCampaignClick = async() => {
        if(applicationStatus !== "approved") {
            notifyUser(null, "error", "Your account must be approved before you can create or manage campaigns.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setCheckingIssuerAgreement(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/entrepreneur/issuer-agreement`,
                { credentials: "include" },
            );
            const agreement = await response.json();

            if(!response.ok) {
                throw new Error(
                    agreement.error ||
                        "Unable to verify the Issuer User Agreement.",
                );
            }

            if(!agreement.accepted) {
                notifyUser(null, "warning", "You must review and electronically sign the Issuer User Agreement before creating or managing a campaign.", "toast", null, null, null, null, 400, false, false, "top-center");
                navigate("/dashboard");
                return;
            }

            if(
                !campaigns ||
                campaigns.length === 0 ||
                campaigns.every((campaign) => campaign.submitted === 0)
            ) {
                navigate("/campaign-page");
                return;
            }

            navigate("/campaign-summary");
        } catch(error) {
            notifyUser(null, "error", error.message, "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setCheckingIssuerAgreement(false);
        }
    };

    return (
        <div
            className="campaign-tracker-container"
            style={{
                padding:
                    windowWidth < 768
                        ? "15px 10px"
                        : windowWidth < 1024
                          ? "18px 15px"
                          : "20px",
                width: "100%",
                height: "100vh",
                minHeight: "100vh",
                maxHeight: "100vh",
                margin: "0",
                marginTop: "0",
                marginLeft: "0",
                boxShadow: "none",
                borderRadius: "0",
                backgroundColor: "transparent",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                maxWidth: "none",
                overflow: "hidden",
            }}
        >
            {/* Table is now shown on CampaignPage, not here */}

            {/* Buttons centered on page */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: windowWidth < 768 ? "20px" : "40px",
                    margin: "0",
                    flexWrap: "wrap",
                    width: "100%",
                    height: "auto",
                }}
            >
                <button
                    onClick={() => {
                        // If no campaigns or all campaigns have submitted=0, go to account-creation
                        if(
                            !campaigns ||
                            campaigns.length === 0 ||
                            campaigns.every((c) => c.submitted === 0)
                        ) {
                            navigate("/entrepreneur-dashboard/account-review");
                            return;
                        }
                        // If any campaign has submitted=1, go to account-review or dashboard based on status
                        const first =
                            campaigns.find((c) => c.submitted === 1) ||
                            campaigns[0];
                        const status = (first.status || "").toLowerCase();
                        if(status === "pending") {
                            navigate("/entrepreneur-dashboard/account-review");
                        } else if(status === "approved") {
                            navigate("/entrepreneur-dashboard");
                        } else {
                            notifyUser(null, "info", "Application status: " + first.status, "toast", null, null, null, null, null, false, false, "top-center");
                        }
                    }}
                    style={{
                        backgroundColor: "#C2D7E2",
                        color: "var(--navy)",
                        border: "none",
                        borderRadius: "18px",
                        padding: windowWidth < 768 ? "25px" : "30px",
                        fontSize: windowWidth < 768 ? "1.4rem" : "1.8rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                        transition: "background 0.2s",
                        width: windowWidth < 768 ? "300px" : "350px",
                        height: windowWidth < 768 ? "200px" : "220px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between",
                        textAlign: "center",
                        lineHeight: 1.2,
                    }}
                >
                    <div style={{ flex: "0 0 auto" }}>Applications</div>
                    <div
                        style={{
                            fontSize: windowWidth < 768 ? "0.85rem" : "1rem",
                            fontWeight: 400,
                            color: "var(--navy)",
                            lineHeight: 1.4,
                            textAlign: "center",
                            padding: "0 10px",
                            flex: "1 1 auto",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        View and manage your entrepreneur account application
                        status, details, and approval progress.
                    </div>
                </button>
                <button
                    onClick={handleCampaignClick}
                    disabled={checkingIssuerAgreement}
                    style={{
                        backgroundColor: "#F0E68C",
                        color: "var(--navy)",
                        border: "none",
                        borderRadius: "18px",
                        padding: windowWidth < 768 ? "25px" : "30px",
                        fontSize: windowWidth < 768 ? "1.4rem" : "1.8rem",
                        fontWeight: 700,
                        cursor: checkingIssuerAgreement
                            ? "not-allowed"
                            : "pointer",
                        opacity: checkingIssuerAgreement ? 0.7 : 1,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                        transition: "background 0.2s",
                        width: windowWidth < 768 ? "300px" : "350px",
                        height: windowWidth < 768 ? "200px" : "220px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between",
                        textAlign: "center",
                        lineHeight: 1.2,
                    }}
                >
                    <div style={{ flex: "0 0 auto" }}>
                        {checkingIssuerAgreement
                            ? "Checking Agreement..."
                            : "Campaigns"}
                    </div>
                    <div
                        style={{
                            fontSize: windowWidth < 768 ? "0.85rem" : "1rem",
                            fontWeight: 400,
                            color: "var(--navy)",
                            lineHeight: 1.4,
                            textAlign: "center",
                            padding: "0 10px",
                            flex: "1 1 auto",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        Access your campaign summary, view details, or start a
                        new campaign if eligible.
                    </div>
                </button>
            </div>

            {/* Chat Modal */}
            {chatModalOpen && selectedCampaign && (
                <CampaignChatModal
                    open={chatModalOpen}
                    onClose={handleCloseChatModal}
                    campaignId={selectedCampaign.id}
                    entrepreneurUser={user}
                />
            )}
        </div>
    );
};

export default CampaignList;
